import React, { useCallback, useEffect, useState } from "react";

import {
  Badge,
  Banner,
  Button,
  ButtonGroup,
  Card,
  Collapsible,
  FormLayout,
  Layout,
  Modal,
  Select,
  SettingToggle,
  Stack,
  Tag,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import { useAdmin } from "context/admin";
import { useUser } from "context/user";
import { fetchData } from "util/fetch";
import { isValidWeightDescriptor } from "util/weightValidation";

const parseBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized.length === 0) return defaultValue;
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const normalizeShopConfig = (config) => {
  if (!config || typeof config !== "object") return config;

  return {
    ...config,
    globepos_overwrite_tags_enabled: parseBoolean(
      config.globepos_overwrite_tags_enabled,
      true,
    ),
    globepos_skip_weighted_items: parseBoolean(
      config.globepos_skip_weighted_items,
      false,
    ),
    globepos_bulk_grocerbox_enabled: parseBoolean(
      config.globepos_bulk_grocerbox_enabled,
      true,
    ),
    globepos_grocerbox_variant_expansion_enabled: parseBoolean(
      config.globepos_grocerbox_variant_expansion_enabled,
      false,
    ),
    globepos_sync_inventory: parseBoolean(config.globepos_sync_inventory, true),
    globepos_update_product_type_enabled: parseBoolean(
      config.globepos_update_product_type_enabled,
      false,
    ),
    globepos_sync_gst_only_collection: parseBoolean(
      config.globepos_sync_gst_only_collection,
      false,
    ),
    globepos_tax_column_mapping:
      config.globepos_tax_column_mapping &&
      typeof config.globepos_tax_column_mapping === "object"
        ? { ...config.globepos_tax_column_mapping }
        : {},
    globepos_create_products: parseBoolean(
      config.globepos_create_products,
      false,
    ),
    globepos_supplement_pack_size: parseBoolean(
      config.globepos_supplement_pack_size,
      false,
    ),
    single_variant_promo_pricing: parseBoolean(
      config.single_variant_promo_pricing,
      false,
    ),
    globepos_new_products_draft: parseBoolean(
      config.globepos_new_products_draft,
      false,
    ),
    orders_organizer_enabled: parseBoolean(
      config.orders_organizer_enabled,
      false,
    ),
    manual_settlement: parseBoolean(config.manual_settlement, false),
    collect_overage_by_invoice: parseBoolean(config.collect_overage_by_invoice, false),
    globepos_special_title_source:
      config.globepos_special_title_source || "DESCRIPTION",
    // Ensure array fields are always arrays
    globepos_blocked_departments: Array.isArray(
      config.globepos_blocked_departments,
    )
      ? config.globepos_blocked_departments
      : [],
    globepos_forced_weight_departments: Array.isArray(
      config.globepos_forced_weight_departments,
    )
      ? config.globepos_forced_weight_departments
      : [],
    globepos_force_fixed_price_units: Array.isArray(
      config.globepos_force_fixed_price_units,
    )
      ? config.globepos_force_fixed_price_units
      : [],
    globepos_forced_scalable_sub_departments: Array.isArray(
      config.globepos_forced_scalable_sub_departments,
    )
      ? config.globepos_forced_scalable_sub_departments
      : [],
    globepos_produce_department_names: Array.isArray(
      config.globepos_produce_department_names,
    )
      ? config.globepos_produce_department_names
      : [],
    globepos_meat_department_names: Array.isArray(
      config.globepos_meat_department_names,
    )
      ? config.globepos_meat_department_names
      : [],
    globepos_deli_department_names: Array.isArray(
      config.globepos_deli_department_names,
    )
      ? config.globepos_deli_department_names
      : [],
    globepos_produce_variant_weights: Array.isArray(
      config.globepos_produce_variant_weights,
    )
      ? config.globepos_produce_variant_weights
      : [],
    globepos_meat_variant_weights: Array.isArray(
      config.globepos_meat_variant_weights,
    )
      ? config.globepos_meat_variant_weights
      : [],
    globepos_deli_variant_weights: Array.isArray(
      config.globepos_deli_variant_weights,
    )
      ? config.globepos_deli_variant_weights
      : [],
    globepos_forced_weight_variant_weights: Array.isArray(
      config.globepos_forced_weight_variant_weights,
    )
      ? config.globepos_forced_weight_variant_weights
      : [],
    globepos_category_description_mappings:
      config.globepos_category_description_mappings &&
      typeof config.globepos_category_description_mappings === "object" &&
      !Array.isArray(config.globepos_category_description_mappings)
        ? config.globepos_category_description_mappings
        : {},
  };
};

const newCategoryMappingGroupId = () =>
  `cg_${Math.random().toString(36).slice(2, 11)}`;

function rowsFromMappingBucket(bucket) {
  if (!bucket || typeof bucket !== "object") return [];
  return Object.entries(bucket)
    .filter(
      ([key]) =>
        key !== "_label" && String(key).toLowerCase() !== "_label",
    )
    .map(([code, description]) => ({
      code: String(code),
      description: String(description ?? ""),
    }));
}

function parseCategoryMappingsUiState(raw) {
  const src =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const departments = [];
  let defaultRows = [];
  Object.entries(src).forEach(([deptKey, bucket]) => {
    if (deptKey === "_default") {
      defaultRows = rowsFromMappingBucket(bucket);
      return;
    }
    if (!bucket || typeof bucket !== "object") return;
    const label =
      bucket._label != null && bucket._label !== undefined
        ? String(bucket._label)
        : "";
    departments.push({
      id: newCategoryMappingGroupId(),
      deptKey: String(deptKey),
      label,
      rows: rowsFromMappingBucket(bucket),
    });
  });
  return { departments, defaultRows };
}

function buildCategoryMappingsPayload({ departments, defaultRows }) {
  const out = {};
  departments.forEach((d) => {
    const dk = (d.deptKey || "").trim();
    if (!dk) return;
    const bucket = {};
    const lab = (d.label || "").trim();
    if (lab) bucket._label = lab;
    (d.rows || []).forEach((r) => {
      const c = (r.code || "").trim();
      if (!c) return;
      const desc = (r.description || "").trim();
      if (!desc) return;
      bucket[c] = desc;
    });
    if (Object.keys(bucket).length > 0) out[dk] = bucket;
  });
  const def = {};
  (defaultRows || []).forEach((r) => {
    const c = (r.code || "").trim();
    if (!c) return;
    const desc = (r.description || "").trim();
    if (!desc) return;
    def[c] = desc;
  });
  if (Object.keys(def).length > 0) out._default = def;
  return out;
}

function parseBulkCategoryMappingLine(line) {
  const t = line.trim();
  if (!t) return null;
  if (t.includes("=")) {
    const i = t.indexOf("=");
    const code = t.slice(0, i).trim();
    const description = t.slice(i + 1).trim();
    return code ? { code, description } : null;
  }
  if (t.includes("\t")) {
    const parts = t.split("\t");
    const code = parts[0].trim();
    const description = parts.slice(1).join("\t").trim();
    return code ? { code, description } : null;
  }
  const multi = t.match(/^(.+?)\s{2,}(.+)$/);
  if (multi) {
    return { code: multi[1].trim(), description: multi[2].trim() };
  }
  const comma = t.indexOf(",");
  if (comma !== -1) {
    return {
      code: t.slice(0, comma).trim(),
      description: t.slice(comma + 1).trim(),
    };
  }
  return null;
}

const GLOBE_POS_CREDENTIAL_KEYS = [
  "globe_pos_cashier",
  "globe_pos_cashier_id",
  "globe_pos_store",
  "globe_pos_terminal",
];

export default function GeneralSettings({ shop_config }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => normalizeShopConfig(shop_config));
  const admin = useAdmin();
  const user = useUser();
  const selectedShop = admin.selectedShop;
  const selectedShopId = admin.selectedShopId;
  const [latestDepartments, setLatestDepartments] = useState(null);
  const [latestDepartmentsLoaded, setLatestDepartmentsLoaded] = useState(false);
  const [latestImportAvailable, setLatestImportAvailable] = useState(false);

  // Sync local form state with updated shop_config prop
  useEffect(() => {
    setForm(normalizeShopConfig(shop_config));
  }, [shop_config]);

  useEffect(() => {
    let canceled = false;

    if (
      !selectedShopId ||
      !form.enable_pos_integration ||
      form.pos !== "globe_pos"
    ) {
      setLatestDepartments(null);
      setLatestDepartmentsLoaded(false);
      setLatestImportAvailable(false);
      return;
    }

    setLatestDepartmentsLoaded(false);

    fetchData(
      `/api/shops/${selectedShopId}/pos_import_histories/latest?provider=globepos`,
      (response) => {
        if (canceled) return;
        setLatestImportAvailable(Boolean(response.import_history));
        setLatestDepartments(response.globepos_departments || {});
        setLatestDepartmentsLoaded(true);
      },
      (error) => {
        if (canceled) return;
        console.error("Failed to load latest POS departments", error);
        setLatestImportAvailable(false);
        setLatestDepartments({});
        setLatestDepartmentsLoaded(true);
      },
    );

    return () => {
      canceled = true;
    };
  }, [selectedShopId, form.enable_pos_integration, form.pos]);

  const handleUpdate = (key, value) => {
    if (key === "globepos_overwrite_tags_enabled") {
      console.debug("[Globepos] Updating overwrite tags flag", {
        outgoingValue: value,
        outgoingType: typeof value,
        previousValue: form.globepos_overwrite_tags_enabled,
        previousType: typeof form.globepos_overwrite_tags_enabled,
      });
    }
    setSaving(key);
    admin
      .updateShopConfig({ [key]: value })
      .then((shopConfig) => setForm(normalizeShopConfig(shopConfig)))
      .finally(() => setSaving(false));
  };

  const handleBulkUpdate = (updates, savingKey = "bulk_update") => {
    setSaving(savingKey);
    return admin
      .updateShopConfig(updates)
      .then((shopConfig) => setForm(normalizeShopConfig(shopConfig)))
      .finally(() => setSaving(false));
  };

  const defaultDepartmentNamePlaceholders = {
    produce: "PRODUCE",
    meat: "MEAT",
    deli: "DELI",
  };

  const defaultVariantWeightPlaceholders = {
    produce: "1kg, 500g",
    meat: "1kg, 500g",
    deli: "200g, 500g",
  };

  const overwriteTagsEnabled = parseBoolean(
    form.globepos_overwrite_tags_enabled,
    true,
  );

  return (
    <>
      <Layout.AnnotatedSection
        title="General Settings"
        description="Configure general shop settings."
      >
        <FormLayout>
          <SettingToggle
            action={{
              content: form.enable_delivery ? "Deactivate" : "Activate",
              onAction: () =>
                handleUpdate("enable_delivery", !form.enable_delivery),
              loading: saving === "enable_delivery",
              destructive: form.enable_delivery,
            }}
            enabled={form.enable_delivery ? true : false}
          >
            Delivery is{" "}
            <Badge status={form.enable_delivery ? "success" : "default"}>
              {form.enable_delivery ? "Activated" : "Deactivated"}
            </Badge>
            .
          </SettingToggle>

          <SettingToggle
            action={{
              content: form.orders_organizer_enabled ? "Disable" : "Enable",
              onAction: () =>
                handleUpdate(
                  "orders_organizer_enabled",
                  !form.orders_organizer_enabled,
                ),
              loading: saving === "orders_organizer_enabled",
            }}
            enabled={!!form.orders_organizer_enabled}
          >
            Orders organizer (department / pickup time filters and sorting) is{" "}
            <Badge
              status={form.orders_organizer_enabled ? "success" : "default"}
            >
              {form.orders_organizer_enabled ? "Enabled" : "Disabled"}
            </Badge>
            . When disabled, the orders list stays simple; customer name search
            still works for all stores.
          </SettingToggle>

          <SettingToggle
            action={{
              content: form.manual_settlement ? "Disable" : "Enable",
              onAction: () =>
                handleUpdate("manual_settlement", !form.manual_settlement),
              loading: saving === "manual_settlement",
            }}
            enabled={!!form.manual_settlement}
          >
            Manual settlement — GrocerBox won't auto-refund or charge. Staff
            settle payments manually in Shopify.{" "}
            <Badge status={form.manual_settlement ? "success" : "default"}>
              {form.manual_settlement ? "Enabled" : "Disabled"}
            </Badge>
          </SettingToggle>

          {form.manual_settlement && (
            <SettingToggle
              action={{
                content: form.collect_overage_by_invoice ? "Disable" : "Enable",
                onAction: () =>
                  handleUpdate(
                    "collect_overage_by_invoice",
                    !form.collect_overage_by_invoice
                  ),
                loading: saving === "collect_overage_by_invoice",
              }}
              enabled={!!form.collect_overage_by_invoice}
            >
              Invoice the balance: when the final total is more than the card
              can cover, have Shopify email the customer an invoice for the
              difference (once per total).{" "}
              <Badge
                status={form.collect_overage_by_invoice ? "success" : "default"}
              >
                {form.collect_overage_by_invoice ? "Enabled" : "Disabled"}
              </Badge>
            </SettingToggle>
          )}
        </FormLayout>

        <FormLayout>
          <TextField
            label="Logo Url"
            value={form.logo_url}
            onChange={(val) => setForm({ ...form, logo_url: val })}
            onBlur={(event) => handleUpdate("logo_url", event.target.value)}
            helpText="Url of logo"
          />
        </FormLayout>
      </Layout.AnnotatedSection>

      {user.isAdmin ? (
        <Layout.AnnotatedSection
          title="POS Settings"
          description="Activate and configure POS integrations."
        >
          <SettingToggle
            action={{
              content: form.enable_pos_integration ? "Deactivate" : "Activate",
              onAction: () =>
                handleUpdate(
                  "enable_pos_integration",
                  !form.enable_pos_integration,
                ),
              loading: saving === "enable_pos_integration",
              destructive: form.enable_pos_integration,
            }}
            enabled={form.enable_pos_integration ? true : false}
          >
            POS integration is{" "}
            <Badge status={form.enable_pos_integration ? "success" : "default"}>
              {form.enable_pos_integration ? "Activated" : "Deactivated"}
            </Badge>
            .
          </SettingToggle>

          {form.enable_pos_integration ? (
            <div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
              <Select
                label="Select POS"
                options={[
                  { label: "ECRS", value: "ecrs" },
                  { label: "Globe POS", value: "globe_pos" },
                  { label: "HDS", value: "hds" },
                ]}
                onChange={(val) => handleUpdate("pos", val)}
                value={form.pos}
              />
            </div>
          ) : (
            ""
          )}

          {form.enable_pos_integration && form.pos === "globe_pos" ? (
            <GlobePosSettings
              form={form}
              setForm={setForm}
              saving={saving}
              handleUpdate={handleUpdate}
              handleBulkUpdate={handleBulkUpdate}
              overwriteTagsEnabled={overwriteTagsEnabled}
              defaultDepartmentNamePlaceholders={
                defaultDepartmentNamePlaceholders
              }
              defaultVariantWeightPlaceholders={
                defaultVariantWeightPlaceholders
              }
              selectedShop={selectedShop}
              latestDepartments={latestDepartments}
              latestDepartmentsLoaded={latestDepartmentsLoaded}
              latestImportAvailable={latestImportAvailable}
            />
          ) : null}
        </Layout.AnnotatedSection>
      ) : (
        ""
      )}

      <Layout.AnnotatedSection
        title="Delivery Settings"
        description="Configure delivery settings."
      >
        <FormLayout>
          <SettingToggle
            action={{
              content: form.disable_automatic_delivery_scheduling
                ? "Enable"
                : "Disable",
              onAction: () =>
                handleUpdate(
                  "disable_automatic_delivery_scheduling",
                  !form.disable_automatic_delivery_scheduling,
                ),
              loading: saving === "disable_automatic_delivery_scheduling",
              destructive: !form.disable_automatic_delivery_scheduling,
            }}
            enabled={!form.disable_automatic_delivery_scheduling}
          >
            Automatic delivery scheduling is{" "}
            <Badge
              status={
                !form.disable_automatic_delivery_scheduling
                  ? "success"
                  : "default"
              }
            >
              {!form.disable_automatic_delivery_scheduling
                ? "Enabled"
                : "Disabled"}
            </Badge>
            .
            {form.disable_automatic_delivery_scheduling && (
              <div
                style={{ marginTop: "8px", fontSize: "14px", color: "#637381" }}
              >
                When disabled, deliveries must be manually scheduled from the
                orders page.
              </div>
            )}
          </SettingToggle>

          <TextField
            label="Delivery Notes"
            value={form.delivery_notes}
            onChange={(val) => setForm({ ...form, delivery_notes: val })}
            onBlur={(event) =>
              handleUpdate("delivery_notes", event.target.value)
            }
            multiline
            helpText="Add instructions for delivery drivers."
          />
        </FormLayout>
      </Layout.AnnotatedSection>
    </>
  );
}

function GlobePosSettings({
  form,
  setForm,
  saving,
  handleUpdate,
  handleBulkUpdate,
  overwriteTagsEnabled,
  defaultDepartmentNamePlaceholders,
  defaultVariantWeightPlaceholders,
  selectedShop,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
}) {
  const [showImportBehaviour, setShowImportBehaviour] = React.useState(false);
  const [showMonitoring, setShowMonitoring] = React.useState(false);
  const [showSpecialImports, setShowSpecialImports] = React.useState(false);
  const [showNormalImport, setShowNormalImport] = React.useState(false);

  return (
    <Stack vertical spacing="tight">
      <GlobePosConnectionCard
        form={form}
        handleBulkUpdate={handleBulkUpdate}
        saving={saving}
      />
      <GlobePosShippingCard
        form={form}
        setForm={setForm}
        handleUpdate={handleUpdate}
      />
      <GlobePosDepartmentNamesCard
        form={form}
        handleBulkUpdate={handleBulkUpdate}
        saving={saving}
        defaultPlaceholders={defaultDepartmentNamePlaceholders}
        latestDepartments={latestDepartments}
        latestDepartmentsLoaded={latestDepartmentsLoaded}
        latestImportAvailable={latestImportAvailable}
      />
      <GlobePosVariantWeightsCard
        form={form}
        handleBulkUpdate={handleBulkUpdate}
        saving={saving}
        defaultPlaceholders={defaultVariantWeightPlaceholders}
      />
      <GlobePosNormalImportCard
        form={form}
        setForm={setForm}
        handleUpdate={handleUpdate}
        saving={saving}
        open={showNormalImport}
        onToggle={() => setShowNormalImport((value) => !value)}
      />
      <GlobePosSpecialImportsCard
        form={form}
        setForm={setForm}
        saving={saving}
        handleUpdate={handleUpdate}
        handleBulkUpdate={handleBulkUpdate}
        latestDepartments={latestDepartments}
        latestDepartmentsLoaded={latestDepartmentsLoaded}
        latestImportAvailable={latestImportAvailable}
        open={showSpecialImports}
        onToggle={() => setShowSpecialImports((value) => !value)}
      />
      <GlobePosImportBehaviourCard
        form={form}
        setForm={setForm}
        handleUpdate={handleUpdate}
        saving={saving}
        overwriteTagsEnabled={overwriteTagsEnabled}
        open={showImportBehaviour}
        onToggle={() => setShowImportBehaviour((value) => !value)}
      />
      <GlobePosMonitoringCard
        form={form}
        setForm={setForm}
        handleUpdate={handleUpdate}
        saving={saving}
        selectedShop={selectedShop}
        open={showMonitoring}
        onToggle={() => setShowMonitoring((value) => !value)}
      />
    </Stack>
  );
}

function GlobePosSpecialImportsCard({
  form,
  setForm,
  saving,
  handleUpdate,
  handleBulkUpdate,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
  open,
  onToggle,
}) {
  return (
    <Card>
      <Card.Section>
        <Stack alignment="center" distribution="between">
          <TextStyle variation="strong">Special File Imports</TextStyle>
          <Button plain onClick={onToggle}>
            {open ? "Hide" : "Show"}
          </Button>
        </Stack>
      </Card.Section>
      <Collapsible open={open} id="globepos-special-imports">
        <Stack vertical spacing="tight">
          <GlobePosUnitsCard
            form={form}
            setForm={setForm}
            handleUpdate={handleUpdate}
          />
          <GlobePosBlockedDepartmentsCard
            form={form}
            handleBulkUpdate={handleBulkUpdate}
            saving={saving}
            latestDepartments={latestDepartments}
            latestDepartmentsLoaded={latestDepartmentsLoaded}
            latestImportAvailable={latestImportAvailable}
          />
          <GlobePosCategoryDescriptionMappingsCard
            form={form}
            handleBulkUpdate={handleBulkUpdate}
            saving={saving}
            latestDepartments={latestDepartments}
            latestDepartmentsLoaded={latestDepartmentsLoaded}
            latestImportAvailable={latestImportAvailable}
          />
          <GlobePosForcedWeightDepartmentsCard
            form={form}
            handleBulkUpdate={handleBulkUpdate}
            saving={saving}
            latestDepartments={latestDepartments}
            latestDepartmentsLoaded={latestDepartmentsLoaded}
            latestImportAvailable={latestImportAvailable}
          />
          <GlobePosForcedWeightVariantWeightsCard
            form={form}
            handleBulkUpdate={handleBulkUpdate}
            saving={saving}
          />
          <GlobePosForceFixedPriceUnitsCard
            form={form}
            handleBulkUpdate={handleBulkUpdate}
            saving={saving}
          />
        </Stack>
      </Collapsible>
    </Card>
  );
}

function GlobePosConnectionCard({ form, handleBulkUpdate, saving }) {
  const [modalOpen, setModalOpen] = React.useState(false);

  const [draft, setDraft] = React.useState(() =>
    buildDraftState(GLOBE_POS_CREDENTIAL_KEYS, form),
  );

  React.useEffect(() => {
    if (modalOpen) {
      setDraft(buildDraftState(GLOBE_POS_CREDENTIAL_KEYS, form));
    }
  }, [modalOpen, form]);

  const handleSave = () =>
    handleBulkUpdate(draft, "globe_pos_credentials").then(() => {
      setModalOpen(false);
    });

  const credentialFields = [
    { key: "globe_pos_cashier", label: "Cashier Number" },
    { key: "globe_pos_cashier_id", label: "Cashier ID" },
    { key: "globe_pos_store", label: "Store Number" },
    { key: "globe_pos_terminal", label: "Terminal Number" },
  ];

  return (
    <>
      <Card>
        <Card.Header
          title="POS Connection"
          actions={[
            {
              content: "Edit credentials",
              onAction: () => setModalOpen(true),
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            {credentialFields.map((field) => (
              <div key={field.key}>
                <TextStyle variation="subdued">{field.label}</TextStyle>
                <div>{form[field.key] ? form[field.key] : "Not set"}</div>
              </div>
            ))}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Edit Globe POS credentials"
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving === "globe_pos_credentials",
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Cashier Number"
              value={draft.globe_pos_cashier}
              onChange={(val) =>
                setDraft((current) => ({
                  ...current,
                  globe_pos_cashier: val,
                }))
              }
            />
            <TextField
              label="Cashier ID"
              value={draft.globe_pos_cashier_id}
              onChange={(val) =>
                setDraft((current) => ({
                  ...current,
                  globe_pos_cashier_id: val,
                }))
              }
            />
            <TextField
              label="Store Number"
              value={draft.globe_pos_store}
              onChange={(val) =>
                setDraft((current) => ({
                  ...current,
                  globe_pos_store: val,
                }))
              }
            />
            <TextField
              label="Terminal Number"
              value={draft.globe_pos_terminal}
              onChange={(val) =>
                setDraft((current) => ({
                  ...current,
                  globe_pos_terminal: val,
                }))
              }
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosShippingCard({ form, setForm, handleUpdate }) {
  return (
    <Card sectioned title="Shipping Codes">
      <FormLayout>
        <TextField
          label="Shipping PLU"
          value={form.globe_shipping_plu || ""}
          onChange={(val) => setForm({ ...form, globe_shipping_plu: val })}
          onBlur={(event) =>
            handleUpdate("globe_shipping_plu", event.target.value)
          }
          autoComplete="off"
        />
        <FormLayout.Group>
          <TextField
            label="Department Number"
            value={form.globe_shipping_dep || ""}
            onChange={(val) => setForm({ ...form, globe_shipping_dep: val })}
            onBlur={(event) =>
              handleUpdate("globe_shipping_dep", event.target.value)
            }
            autoComplete="off"
          />
          <TextField
            label="Sub Department Number"
            value={form.globe_shipping_sub_dep || ""}
            onChange={(val) =>
              setForm({ ...form, globe_shipping_sub_dep: val })
            }
            onBlur={(event) =>
              handleUpdate("globe_shipping_sub_dep", event.target.value)
            }
            autoComplete="off"
          />
        </FormLayout.Group>
      </FormLayout>
    </Card>
  );
}

function GlobePosUnitsCard({ form, setForm, handleUpdate }) {
  const unitOptions = [
    { label: "Kilograms (kg)", value: "kg" },
    { label: "Pounds (lb)", value: "lb" },
  ];

  const handleSelectChange = (key) => (value) => {
    setForm({ ...form, [key]: value });
    handleUpdate(key, value);
  };

  return (
    <Card sectioned title="Weighted Item Units">
      <FormLayout>
        <Select
          label="Default Scalable Unit"
          options={unitOptions}
          value={form.globe_scalable_unit || "lb"}
          onChange={handleSelectChange("globe_scalable_unit")}
        />
        <FormLayout.Group>
          <Select
            label="Produce Unit"
            options={unitOptions}
            value={form.produce_scalable_unit || "lb"}
            onChange={handleSelectChange("produce_scalable_unit")}
          />
          <Select
            label="Meat Unit"
            options={unitOptions}
            value={form.meat_scalable_unit || "lb"}
            onChange={handleSelectChange("meat_scalable_unit")}
          />
        </FormLayout.Group>
      </FormLayout>
    </Card>
  );
}

function GlobePosDepartmentNamesCard({
  form,
  handleBulkUpdate,
  saving,
  defaultPlaceholders,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
}) {
  const [activeDepartment, setActiveDepartment] = React.useState("produce");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [aliasesDraft, setAliasesDraft] = React.useState([]);
  const [newAlias, setNewAlias] = React.useState("");
  const [selectedSuggestion, setSelectedSuggestion] = React.useState("");

  const departments = React.useMemo(
    () => [
      {
        key: "produce",
        label: "Produce",
        field: "globepos_produce_department_names",
        placeholder: defaultPlaceholders.produce,
      },
      {
        key: "meat",
        label: "Meat",
        field: "globepos_meat_department_names",
        placeholder: defaultPlaceholders.meat,
      },
      {
        key: "deli",
        label: "Deli",
        field: "globepos_deli_department_names",
        placeholder: defaultPlaceholders.deli,
      },
    ],
    [defaultPlaceholders],
  );

  const normalizeAlias = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .toUpperCase();
  }, []);

  const suggestionOptions = React.useMemo(() => {
    const values = new Set();

    if (latestDepartments && typeof latestDepartments === "object") {
      [
        "sub_department_names",
        "department_names",
        "sub_department_numbers",
        "department_numbers",
      ].forEach((key) => {
        const list = latestDepartments[key];
        if (Array.isArray(list)) {
          list.forEach((item) => {
            const normalized = normalizeAlias(item);
            if (normalized.length > 0) {
              values.add(normalized);
            }
          });
        }
      });
    }

    const sorted = Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return [
      { label: "Select department from latest import", value: "" },
      ...sorted.map((value) => ({ label: value, value })),
    ];
  }, [latestDepartments, normalizeAlias]);

  const openModal = (departmentKey) => {
    setActiveDepartment(departmentKey);
    seedDraftFromDepartment(departmentKey);
    setModalOpen(true);
    setSelectedSuggestion("");
  };

  const seedDraftFromDepartment = (departmentKey) => {
    const config = departments.find((dept) => dept.key === departmentKey);
    if (!config) return;
    const existing = Array.isArray(form[config.field])
      ? form[config.field]
      : [];
    setAliasesDraft(existing.map((value) => normalizeAlias(value)));
  };
  const handleDepartmentChange = (value) => {
    setActiveDepartment(value);
    seedDraftFromDepartment(value);
    setSelectedSuggestion("");
  };

  const handleAddAlias = () => {
    const normalized = normalizeAlias(newAlias);
    if (!normalized) return;
    if (
      aliasesDraft.some(
        (alias) => alias.toLowerCase() === normalized.toLowerCase(),
      )
    ) {
      setNewAlias("");
      return;
    }
    setAliasesDraft((current) => [...current, normalized]);
    setNewAlias("");
  };

  const handleRemoveAlias = (alias) => {
    setAliasesDraft((current) => current.filter((item) => item !== alias));
  };

  const handleSaveAliases = () => {
    const config = departments.find((dept) => dept.key === activeDepartment);
    if (!config) return;
    const uniqueAliases = Array.from(
      new Set(aliasesDraft.map((value) => normalizeAlias(value))),
    ).filter((value) => value.length > 0);
    handleBulkUpdate({ [config.field]: uniqueAliases }, config.field).then(() =>
      setModalOpen(false),
    );
  };

  const handleAddSuggestion = (value) => {
    const normalized = normalizeAlias(value);
    if (!normalized) return;
    if (
      aliasesDraft.some(
        (alias) => alias.toLowerCase() === normalized.toLowerCase(),
      )
    ) {
      setSelectedSuggestion("");
      return;
    }
    setAliasesDraft((current) => [...current, normalized]);
    setSelectedSuggestion("");
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Department Matching"
          actions={[
            {
              content: "Manage",
              onAction: () => openModal(activeDepartment || "produce"),
            },
          ]}
        />
        <Card.Section>
          <TextStyle variation="subdued">
            Map department names from your POS to produce, meat, and deli
            categories. This is used by both special file imports and normal
            imports (when variant expansion is enabled).
          </TextStyle>
        </Card.Section>
        <Card.Section>
          {latestDepartmentsLoaded && (
            <div style={{ marginBottom: "0.75rem" }}>
              {latestImportAvailable ? (
                Array.isArray(latestDepartments?.sub_department_names) &&
                latestDepartments.sub_department_names.length > 0 ? (
                  <Stack vertical spacing="extraTight">
                    <TextStyle variation="subdued">
                      Latest import sub-department names:
                    </TextStyle>
                    <Stack spacing="tight" wrap>
                      {latestDepartments.sub_department_names
                        .slice(0, 12)
                        .map((name) => (
                          <Tag key={`latest-sub-${name}`}>{name}</Tag>
                        ))}
                    </Stack>
                    {latestDepartments.sub_department_names.length > 12 && (
                      <TextStyle variation="subdued">
                        +{latestDepartments.sub_department_names.length - 12}{" "}
                        more
                      </TextStyle>
                    )}
                  </Stack>
                ) : (
                  <TextStyle variation="subdued">
                    Latest import did not include department metadata.
                  </TextStyle>
                )
              ) : (
                <TextStyle variation="subdued">
                  No GlobePOS imports processed yet.
                </TextStyle>
              )}
            </div>
          )}
          <Stack vertical spacing="extraTight">
            {departments.map((dept) => {
              const currentAliases = Array.isArray(form[dept.field])
                ? form[dept.field]
                : [];
              const previewValues = currentAliases.length
                ? currentAliases
                : dept.placeholder.split(",").map((value) => value.trim());
              return (
                <Stack key={dept.key} alignment="center" spacing="tight">
                  <Stack.Item>
                    <div style={{ minWidth: 80 }}>
                      <TextStyle variation="strong">{dept.label}</TextStyle>
                    </div>
                  </Stack.Item>
                  <Stack.Item fill>
                    <Stack spacing="tight" wrap>
                      {previewValues.map((value) => (
                        <Tag key={`${dept.key}-${value}`}>{value}</Tag>
                      ))}
                      {!currentAliases.length && (
                        <TextStyle variation="subdued">
                          Using defaults
                        </TextStyle>
                      )}
                    </Stack>
                  </Stack.Item>
                </Stack>
              );
            })}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        title="Manage department names"
        onClose={() => setModalOpen(false)}
        primaryAction={{
          content: "Save",
          onAction: handleSaveAliases,
          loading:
            activeDepartment &&
            saving === `globepos_${activeDepartment}_department_names`,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Enter any alternate department names that should map to this
              category in the ITEMFILE.
            </TextStyle>
            <FormLayout>
              <Select
                label="Department"
                options={departments.map((dept) => ({
                  label: dept.label,
                  value: dept.key,
                }))}
                value={activeDepartment}
                onChange={handleDepartmentChange}
              />
            </FormLayout>
            <Stack spacing="tight" wrap>
              {aliasesDraft.length === 0 ? (
                <TextStyle variation="subdued">
                  No custom names added.
                </TextStyle>
              ) : (
                aliasesDraft.map((alias) => (
                  <Tag key={alias} onRemove={() => handleRemoveAlias(alias)}>
                    {alias}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              {suggestionOptions.length > 1 && (
                <FormLayout.Group>
                  <Select
                    label="Add from latest import"
                    options={suggestionOptions}
                    value={selectedSuggestion}
                    onChange={setSelectedSuggestion}
                  />
                  <Button
                    onClick={() => handleAddSuggestion(selectedSuggestion)}
                    disabled={!selectedSuggestion}
                    primary
                  >
                    Add selected
                  </Button>
                </FormLayout.Group>
              )}
              <FormLayout.Group>
                <TextField
                  label="Add department name"
                  value={newAlias}
                  onChange={setNewAlias}
                  autoComplete="off"
                />
                <Button onClick={handleAddAlias} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosBlockedDepartmentsCard({
  form,
  handleBulkUpdate,
  saving,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draftValues, setDraftValues] = React.useState([]);
  const [newValue, setNewValue] = React.useState("");
  const [selectedSuggestion, setSelectedSuggestion] = React.useState("");

  const normalizeValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .toUpperCase();
  }, []);

  const currentValues = React.useMemo(() => {
    const raw = Array.isArray(form.globepos_blocked_departments)
      ? form.globepos_blocked_departments
      : [];
    return raw
      .map((value) => normalizeValue(value))
      .filter((value) => value.length > 0);
  }, [form.globepos_blocked_departments, normalizeValue]);

  const suggestionOptions = React.useMemo(() => {
    const values = new Set();

    if (latestDepartments && typeof latestDepartments === "object") {
      [
        "sub_department_names",
        "department_names",
        "sub_department_numbers",
        "department_numbers",
      ].forEach((key) => {
        const list = latestDepartments[key];
        if (Array.isArray(list)) {
          list.forEach((item) => {
            const normalized = normalizeValue(item);
            if (normalized.length > 0) {
              values.add(normalized);
            }
          });
        }
      });
    }

    const sorted = Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return [
      { label: "Select value from latest import", value: "" },
      ...sorted.map((value) => ({ label: value, value })),
    ];
  }, [latestDepartments, normalizeValue]);

  const openModal = () => {
    setDraftValues(currentValues);
    setNewValue("");
    setSelectedSuggestion("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleAddValue = (value) => {
    const normalized = normalizeValue(value);
    if (!normalized) return;
    if (draftValues.some((existing) => existing === normalized)) {
      return;
    }
    setDraftValues((prev) => [...prev, normalized]);
  };

  const handleRemove = (value) => {
    setDraftValues((prev) => prev.filter((item) => item !== value));
  };

  const handleSave = () => {
    const unique = Array.from(
      new Set(draftValues.map((value) => normalizeValue(value))),
    ).filter((value) => value.length > 0);

    handleBulkUpdate(
      { globepos_blocked_departments: unique },
      "globepos_blocked_departments",
    ).then(() => {
      setModalOpen(false);
    });
  };

  const handleAddSuggestion = () => {
    if (!selectedSuggestion) return;
    handleAddValue(selectedSuggestion);
    setSelectedSuggestion("");
  };

  const handleSubmitNewValue = () => {
    handleAddValue(newValue);
    setNewValue("");
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Blocked Departments"
          actions={[
            {
              content: "Manage",
              onAction: () => openModal(),
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Rows from the ITEMFILE whose department number or name matches any
              value here will be skipped during import. Prefer SUB DEPARTMENT # or
              name over raw category codes — category descriptions may be rewritten
              by category code mappings (special import).
            </TextStyle>
            {currentValues.length > 0 ? (
              <Stack spacing="tight" wrap>
                {currentValues.slice(0, 12).map((value) => (
                  <Tag key={`blocked-${value}`}>{value}</Tag>
                ))}
                {currentValues.length > 12 && (
                  <TextStyle variation="subdued">
                    +{currentValues.length - 12} more
                  </TextStyle>
                )}
              </Stack>
            ) : (
              <TextStyle variation="subdued">No departments blocked.</TextStyle>
            )}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Manage blocked departments"
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving === "globepos_blocked_departments",
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Add department numbers or names to keep them out of special
              imports.
            </TextStyle>
            {latestDepartmentsLoaded &&
              latestImportAvailable &&
              Array.isArray(latestDepartments?.department_names) &&
              latestDepartments.department_names.length > 0 && (
                <Stack vertical spacing="extraTight">
                  <TextStyle variation="subdued">
                    Sample department names from latest import:
                  </TextStyle>
                  <Stack spacing="tight" wrap>
                    {latestDepartments.department_names
                      .slice(0, 8)
                      .map((name) => (
                        <Tag key={`latest-blocked-${name}`}>{name}</Tag>
                      ))}
                  </Stack>
                </Stack>
              )}
            <Stack spacing="tight" wrap>
              {draftValues.length === 0 ? (
                <TextStyle variation="subdued">
                  No blocked departments selected.
                </TextStyle>
              ) : (
                draftValues.map((value) => (
                  <Tag
                    key={`manage-blocked-${value}`}
                    onRemove={() => handleRemove(value)}
                  >
                    {value}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              {suggestionOptions.length > 1 && (
                <FormLayout.Group>
                  <Select
                    label="Add from latest import"
                    options={suggestionOptions}
                    value={selectedSuggestion}
                    onChange={setSelectedSuggestion}
                  />
                  <Button
                    onClick={handleAddSuggestion}
                    disabled={!selectedSuggestion}
                    primary
                  >
                    Add selected
                  </Button>
                </FormLayout.Group>
              )}
              <FormLayout.Group>
                <TextField
                  label="Add department name or number"
                  value={newValue}
                  onChange={setNewValue}
                  autoComplete="off"
                />
                <Button onClick={handleSubmitNewValue} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosCategoryDescriptionMappingsCard({
  form,
  handleBulkUpdate,
  saving,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
}) {
  const synced = React.useMemo(
    () => parseCategoryMappingsUiState(form.globepos_category_description_mappings),
    [form.globepos_category_description_mappings],
  );

  const [departments, setDepartments] = React.useState(() => synced.departments);
  const [defaultRows, setDefaultRows] = React.useState(() =>
    synced.defaultRows.length > 0
      ? synced.defaultRows
      : [{ code: "", description: "" }],
  );
  const [saveError, setSaveError] = React.useState("");
  const [collisionWarning, setCollisionWarning] = React.useState("");
  const [pasteModalOpen, setPasteModalOpen] = React.useState(false);
  const [pasteTarget, setPasteTarget] = React.useState(null);
  const [pasteText, setPasteText] = React.useState("");
  const [showDefaultSection, setShowDefaultSection] = React.useState(true);

  React.useEffect(() => {
    const next = parseCategoryMappingsUiState(
      form.globepos_category_description_mappings,
    );
    setDepartments(next.departments);
    setDefaultRows(
      next.defaultRows.length > 0
        ? next.defaultRows
        : [{ code: "", description: "" }],
    );
  }, [form.globepos_category_description_mappings]);

  const listTokens = React.useMemo(() => {
    const a = [
      ...(Array.isArray(form.globepos_blocked_departments)
        ? form.globepos_blocked_departments
        : []),
      ...(Array.isArray(form.globepos_forced_weight_departments)
        ? form.globepos_forced_weight_departments
        : []),
      ...(Array.isArray(form.globepos_forced_scalable_sub_departments)
        ? form.globepos_forced_scalable_sub_departments
        : []),
    ];
    return new Set(
      a.map((x) => String(x).trim().toUpperCase()).filter(Boolean),
    );
  }, [
    form.globepos_blocked_departments,
    form.globepos_forced_weight_departments,
    form.globepos_forced_scalable_sub_departments,
  ]);

  const mergeParsedRows = (existing, parsed) => {
    const map = new Map();
    (existing || []).forEach((r) => {
      const c = (r.code || "").trim();
      if (c) map.set(c, (r.description || "").trim());
    });
    (parsed || []).forEach(({ code, description }) => {
      const c = (code || "").trim();
      const d = (description || "").trim();
      if (!c || !d) return;
      map.set(c, d);
    });
    return Array.from(map.entries()).map(([code, description]) => ({
      code,
      description,
    }));
  };

  const collectCodes = (depts, defRows) => {
    const codes = [];
    (depts || []).forEach((d) =>
      (d.rows || []).forEach((r) => {
        const c = (r.code || "").trim();
        if (c) codes.push(c);
      }),
    );
    (defRows || []).forEach((r) => {
      const c = (r.code || "").trim();
      if (c) codes.push(c);
    });
    return codes;
  };

  const handleSave = () => {
    setSaveError("");
    const payload = buildCategoryMappingsPayload({ departments, defaultRows });
    const codes = collectCodes(departments, defaultRows);
    const hits = [
      ...new Set(
        codes.filter((c) => listTokens.has(c.toUpperCase())),
      ),
    ];
    setCollisionWarning(
      hits.length
        ? `These codes also appear in blocked or forced department lists and may stop matching after import: ${hits.join(", ")}. Prefer SUB DEPARTMENT # or name in those lists.`
        : "",
    );
    return handleBulkUpdate(
      { globepos_category_description_mappings: payload },
      "globepos_category_mappings",
    ).catch((err) => {
      setSaveError(err.message || "Save failed");
    });
  };

  const addDepartment = () => {
    setDepartments((d) => [
      ...d,
      {
        id: newCategoryMappingGroupId(),
        deptKey: "",
        label: "",
        rows: [{ code: "", description: "" }],
      },
    ]);
  };

  const removeDepartment = (id) => {
    if (
      !window.confirm(
        "Remove this department group and all its category rows?",
      )
    ) {
      return;
    }
    setDepartments((d) => d.filter((g) => g.id !== id));
  };

  const updateDept = (id, patch) => {
    setDepartments((d) =>
      d.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    );
  };

  const updateRow = (deptId, index, patch) => {
    setDepartments((d) =>
      d.map((g) => {
        if (g.id !== deptId) return g;
        const rows = (g.rows || []).map((r, i) =>
          i === index ? { ...r, ...patch } : r,
        );
        return { ...g, rows };
      }),
    );
  };

  const addRow = (deptId) => {
    setDepartments((d) =>
      d.map((g) =>
        g.id === deptId
          ? { ...g, rows: [...(g.rows || []), { code: "", description: "" }] }
          : g,
      ),
    );
  };

  const removeRow = (deptId, index) => {
    setDepartments((d) =>
      d.map((g) => {
        if (g.id !== deptId) return g;
        const rows = (g.rows || []).filter((_, i) => i !== index);
        return { ...g, rows: rows.length ? rows : [{ code: "", description: "" }] };
      }),
    );
  };

  const updateDefaultRow = (index, patch) => {
    setDefaultRows((rows) =>
      (rows || []).map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const addDefaultRow = () => {
    setDefaultRows((rows) => [
      ...(rows || []),
      { code: "", description: "" },
    ]);
  };

  const removeDefaultRow = (index) => {
    setDefaultRows((rows) => {
      const next = (rows || []).filter((_, i) => i !== index);
      return next.length ? next : [{ code: "", description: "" }];
    });
  };

  const openPasteModal = (targetId) => {
    setPasteTarget(targetId);
    setPasteText("");
    setPasteModalOpen(true);
  };

  const applyPaste = () => {
    const lines = pasteText.split(/\r?\n/);
    const parsed = lines.map(parseBulkCategoryMappingLine).filter(Boolean);
    if (pasteTarget === "default") {
      setDefaultRows((r) => mergeParsedRows(r || [], parsed));
    } else {
      setDepartments((ds) =>
        ds.map((g) =>
          g.id === pasteTarget
            ? { ...g, rows: mergeParsedRows(g.rows, parsed) }
            : g,
        ),
      );
    }
    setPasteModalOpen(false);
    setPasteText("");
    setPasteTarget(null);
  };

  const hintKeys = [
    "sub_department_numbers",
    "sub_department_names",
    "observed_categories",
  ];

  return (
    <>
      <Card>
        <Card.Header
          title="Category code mappings (special import)"
          actions={[
            {
              content: "Save mappings",
              onAction: handleSave,
              loading: saving === "globepos_category_mappings",
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            {saveError ? (
              <Banner status="critical" onDismiss={() => setSaveError("")}>
                {saveError}
              </Banner>
            ) : null}
            {collisionWarning ? (
              <Banner status="warning">{collisionWarning}</Banner>
            ) : null}
            <TextStyle variation="subdued">
              Map numeric CATEGORY # / CATEGORY DESCRIPTION codes to readable text
              per SUB DEPARTMENT # (or name). Used only for GlobePOS special
              ITEMFILE imports. Codes can repeat across departments — use a separate
              group for each SUB DEPARTMENT #.
            </TextStyle>
            {latestDepartmentsLoaded && latestImportAvailable ? (
              <Stack vertical spacing="extraTight">
                <TextStyle variation="subdued">
                  Latest import hints (SUB DEPT #, names, raw category values):
                </TextStyle>
                <Stack spacing="tight" wrap>
                  {hintKeys.flatMap((key) => {
                    const list = latestDepartments[key];
                    if (!Array.isArray(list)) return [];
                    return list.slice(0, 8).map((name) => (
                      <Tag key={`${key}-${name}`}>{String(name)}</Tag>
                    ));
                  })}
                </Stack>
              </Stack>
            ) : null}
            <Button primary onClick={addDepartment}>
              Add department group
            </Button>
          </Stack>
        </Card.Section>
        {departments.map((g) => (
          <Card.Section key={g.id}>
            <Stack vertical spacing="tight">
              <Stack
                distribution="equalSpacing"
                alignment="center"
                wrap={false}
              >
                <div style={{ flex: "1 1 200px" }}>
                  <TextStyle variation="strong">
                    {(g.label || "").trim() || "Department"}{" "}
                    <TextStyle variation="subdued">
                      {(g.deptKey || "").trim()
                        ? `(SUB DEPT # ${(g.deptKey || "").trim()})`
                        : ""}
                    </TextStyle>
                  </TextStyle>
                </div>
                <Button plain destructive onClick={() => removeDepartment(g.id)}>
                  Remove group
                </Button>
              </Stack>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="SUB DEPARTMENT # (key)"
                    value={g.deptKey}
                    onChange={(v) => updateDept(g.id, { deptKey: v })}
                    autoComplete="off"
                    helpText="Must match ITEMFILE SUB DEPARTMENT #"
                  />
                  <TextField
                    label="Label (optional, display only)"
                    value={g.label}
                    onChange={(v) => updateDept(g.id, { label: v })}
                    autoComplete="off"
                  />
                </FormLayout.Group>
              </FormLayout>
              <Button onClick={() => openPasteModal(g.id)}>
                Paste mappings (bulk)
              </Button>
              <Stack vertical spacing="tight">
                {(g.rows || []).map((row, idx) => (
                  <FormLayout key={`${g.id}-row-${idx}`}>
                    <FormLayout.Group>
                      <TextField
                        label="Code"
                        value={row.code}
                        onChange={(v) => updateRow(g.id, idx, { code: v })}
                        autoComplete="off"
                      />
                      <TextField
                        label="Description (tag text)"
                        value={row.description}
                        onChange={(v) =>
                          updateRow(g.id, idx, { description: v })
                        }
                        autoComplete="off"
                        helpText={
                          (row.description || "").includes(",")
                            ? "Commas can split into multiple cached tags downstream; consider & or /."
                            : ""
                        }
                      />
                    </FormLayout.Group>
                    <Button
                      plain
                      destructive
                      onClick={() => removeRow(g.id, idx)}
                    >
                      Remove row
                    </Button>
                  </FormLayout>
                ))}
              </Stack>
              <Button onClick={() => addRow(g.id)}>Add row</Button>
            </Stack>
          </Card.Section>
        ))}
        <Card.Section>
          <Stack vertical spacing="tight">
            <Button
              plain
              onClick={() => setShowDefaultSection((s) => !s)}
            >
              {showDefaultSection ? "Hide" : "Show"} default mappings (_default)
            </Button>
            <Collapsible
              open={showDefaultSection}
              id="globepos-category-default"
            >
              <Stack vertical spacing="tight">
                <TextStyle variation="subdued">
                  Applied when no department bucket matches (e.g. store-wide
                  codes).
                </TextStyle>
                <Button onClick={() => openPasteModal("default")}>
                  Paste mappings (bulk)
                </Button>
                {(defaultRows || []).map((row, idx) => (
                  <FormLayout key={`def-${idx}`}>
                    <FormLayout.Group>
                      <TextField
                        label="Code"
                        value={row.code}
                        onChange={(v) => updateDefaultRow(idx, { code: v })}
                        autoComplete="off"
                      />
                      <TextField
                        label="Description"
                        value={row.description}
                        onChange={(v) =>
                          updateDefaultRow(idx, { description: v })
                        }
                        autoComplete="off"
                        helpText={
                          (row.description || "").includes(",")
                            ? "Commas may split cached tags; consider & or /."
                            : ""
                        }
                      />
                    </FormLayout.Group>
                    <Button
                      plain
                      destructive
                      onClick={() => removeDefaultRow(idx)}
                    >
                      Remove row
                    </Button>
                  </FormLayout>
                ))}
                <Button onClick={addDefaultRow}>Add row</Button>
              </Stack>
            </Collapsible>
          </Stack>
        </Card.Section>
        {departments.length === 0 &&
        (defaultRows || []).every((r) => !(r.code || "").trim()) ? (
          <Card.Section>
            <TextStyle variation="subdued">
              No mappings yet. Add a department (SUB DEPARTMENT # from your
              ITEMFILE), then add category codes — or use Paste mappings.
            </TextStyle>
          </Card.Section>
        ) : null}
      </Card>
      <Modal
        open={pasteModalOpen}
        onClose={() => {
          setPasteModalOpen(false);
          setPasteText("");
          setPasteTarget(null);
        }}
        title="Paste mappings"
        primaryAction={{
          content: "Merge into group",
          onAction: applyPaste,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => {
              setPasteModalOpen(false);
              setPasteText("");
              setPasteTarget(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              One per line: code=description, code,description, tab-separated, or
              code then two+ spaces then description. Later lines override duplicate
              codes.
            </TextStyle>
            <TextField
              label="Mappings"
              value={pasteText}
              onChange={setPasteText}
              multiline={6}
              autoComplete="off"
            />
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosForcedWeightDepartmentsCard({
  form,
  handleBulkUpdate,
  saving,
  latestDepartments,
  latestDepartmentsLoaded,
  latestImportAvailable,
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draftValues, setDraftValues] = React.useState([]);
  const [newValue, setNewValue] = React.useState("");
  const [selectedSuggestion, setSelectedSuggestion] = React.useState("");

  const normalizeValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .toUpperCase();
  }, []);

  const currentValues = React.useMemo(() => {
    const raw = Array.isArray(form.globepos_forced_weight_departments)
      ? form.globepos_forced_weight_departments
      : [];
    return raw
      .map((value) => normalizeValue(value))
      .filter((value) => value.length > 0);
  }, [form.globepos_forced_weight_departments, normalizeValue]);

  const suggestionOptions = React.useMemo(() => {
    const values = new Set();

    if (latestDepartments && typeof latestDepartments === "object") {
      [
        "sub_department_names",
        "department_names",
        "sub_department_numbers",
        "department_numbers",
      ].forEach((key) => {
        const list = latestDepartments[key];
        if (Array.isArray(list)) {
          list.forEach((item) => {
            const normalized = normalizeValue(item);
            if (normalized.length > 0) {
              values.add(normalized);
            }
          });
        }
      });
    }

    const sorted = Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return [
      { label: "Select value from latest import", value: "" },
      ...sorted.map((value) => ({ label: value, value })),
    ];
  }, [latestDepartments, normalizeValue]);

  const openModal = () => {
    setDraftValues(currentValues);
    setNewValue("");
    setSelectedSuggestion("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleAddValue = (value) => {
    const normalized = normalizeValue(value);
    if (!normalized) return;
    if (draftValues.some((existing) => existing === normalized)) {
      return;
    }
    setDraftValues((prev) => [...prev, normalized]);
  };

  const handleRemove = (value) => {
    setDraftValues((prev) => prev.filter((item) => item !== value));
  };

  const handleSave = () => {
    const unique = Array.from(
      new Set(draftValues.map((value) => normalizeValue(value))),
    ).filter((value) => value.length > 0);

    handleBulkUpdate(
      { globepos_forced_weight_departments: unique },
      "globepos_forced_weight_departments",
    ).then(() => {
      setModalOpen(false);
    });
  };

  const handleAddSuggestion = () => {
    if (!selectedSuggestion) return;
    handleAddValue(selectedSuggestion);
    setSelectedSuggestion("");
  };

  const handleSubmitNewValue = () => {
    handleAddValue(newValue);
    setNewValue("");
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Forced Weight Departments"
          actions={[
            {
              content: "Manage",
              onAction: () => openModal(),
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Items from these departments will always be treated as weighted
              (sold by weight with 250g/500g/1kg variants), regardless of their
              SCALABLE flag or PLU type. Prefer SUB DEPARTMENT # or name over raw
              category codes — category descriptions may be rewritten by category
              code mappings.
            </TextStyle>
            {currentValues.length > 0 ? (
              <Stack spacing="tight" wrap>
                {currentValues.slice(0, 12).map((value) => (
                  <Tag key={`forced-weight-${value}`}>{value}</Tag>
                ))}
                {currentValues.length > 12 && (
                  <TextStyle variation="subdued">
                    +{currentValues.length - 12} more
                  </TextStyle>
                )}
              </Stack>
            ) : (
              <TextStyle variation="subdued">
                No forced weight departments configured.
              </TextStyle>
            )}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Manage forced weight departments"
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving === "globepos_forced_weight_departments",
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Add department numbers or names to force items to be treated as
              weighted (sold by weight with multiple variants).
            </TextStyle>
            {latestDepartmentsLoaded &&
              latestImportAvailable &&
              Array.isArray(latestDepartments?.department_names) &&
              latestDepartments.department_names.length > 0 && (
                <Stack vertical spacing="extraTight">
                  <TextStyle variation="subdued">
                    Sample department names from latest import:
                  </TextStyle>
                  <Stack spacing="tight" wrap>
                    {latestDepartments.department_names
                      .slice(0, 8)
                      .map((name) => (
                        <Tag key={`latest-forced-weight-${name}`}>{name}</Tag>
                      ))}
                  </Stack>
                </Stack>
              )}
            <Stack spacing="tight" wrap>
              {draftValues.length === 0 ? (
                <TextStyle variation="subdued">
                  No forced weight departments selected.
                </TextStyle>
              ) : (
                draftValues.map((value) => (
                  <Tag
                    key={`manage-forced-weight-${value}`}
                    onRemove={() => handleRemove(value)}
                  >
                    {value}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              {suggestionOptions.length > 1 && (
                <FormLayout.Group>
                  <Select
                    label="Add from latest import"
                    options={suggestionOptions}
                    value={selectedSuggestion}
                    onChange={setSelectedSuggestion}
                  />
                  <Button
                    onClick={handleAddSuggestion}
                    disabled={!selectedSuggestion}
                    primary
                  >
                    Add selected
                  </Button>
                </FormLayout.Group>
              )}
              <FormLayout.Group>
                <TextField
                  label="Add department name or number"
                  value={newValue}
                  onChange={setNewValue}
                  autoComplete="off"
                />
                <Button onClick={handleSubmitNewValue} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosVariantWeightsCard({
  form,
  handleBulkUpdate,
  saving,
  defaultPlaceholders,
}) {
  const [activeDepartment, setActiveDepartment] = React.useState(null);
  const [weightsDraft, setWeightsDraft] = React.useState([]);
  const [newWeight, setNewWeight] = React.useState("");
  const [error, setError] = React.useState(null);

  const departments = React.useMemo(
    () => [
      {
        key: "produce",
        label: "Produce",
        field: "globepos_produce_variant_weights",
        placeholder: defaultPlaceholders.produce,
      },
      {
        key: "meat",
        label: "Meat",
        field: "globepos_meat_variant_weights",
        placeholder: defaultPlaceholders.meat,
      },
      {
        key: "deli",
        label: "Deli",
        field: "globepos_deli_variant_weights",
        placeholder: defaultPlaceholders.deli,
      },
    ],
    [defaultPlaceholders],
  );

  React.useEffect(() => {
    if (!activeDepartment) return;
    const config = departments.find((dept) => dept.key === activeDepartment);
    if (!config) return;
    const existing = Array.isArray(form[config.field])
      ? form[config.field]
      : [];
    setWeightsDraft(existing);
    setError(null);
  }, [activeDepartment, form, departments]);

  const handleAddWeight = () => {
    const trimmed = newWeight.trim();
    if (!trimmed) return;
    if (!isValidWeightDescriptor(trimmed)) {
      setError("Use format like 1kg, 500g, or 1lb (include number and unit).");
      return;
    }
    if (
      weightsDraft.some(
        (descriptor) => descriptor.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setNewWeight("");
      setError(null);
      return;
    }
    setWeightsDraft((current) => [...current, trimmed]);
    setNewWeight("");
    setError(null);
  };

  const handleRemoveWeight = (descriptor) => {
    setWeightsDraft((current) => current.filter((item) => item !== descriptor));
  };

  const handleSaveWeights = () => {
    if (!activeDepartment) return;
    const config = departments.find((dept) => dept.key === activeDepartment);
    if (!config) return;
    handleBulkUpdate({ [config.field]: weightsDraft }, config.field).then(() =>
      setActiveDepartment(null),
    );
  };

  return (
    <>
      <Card>
        <Card.Header title="Variant Weights" />
        <Card.Section>
          <TextStyle variation="subdued">
            Configure weight variants for produce, meat, and deli products.
            These weights are used by both special file imports and normal
            imports (when variant expansion is enabled).
          </TextStyle>
        </Card.Section>
        <Card.Section>
          <Stack vertical spacing="tight">
            {departments.map((dept) => {
              const currentWeights = Array.isArray(form[dept.field])
                ? form[dept.field]
                : [];
              const previewValues = currentWeights.length
                ? currentWeights
                : dept.placeholder.split(",").map((value) => value.trim());
              return (
                <Stack key={dept.key} alignment="center" distribution="between">
                  <div>
                    <TextStyle variation="strong">{dept.label}</TextStyle>
                    <div>
                      <Stack spacing="tight" wrap>
                        {previewValues.map((value) => (
                          <Tag key={`${dept.key}-${value}`}>{value}</Tag>
                        ))}
                      </Stack>
                      {!currentWeights.length && (
                        <TextStyle variation="subdued">
                          Using defaults
                        </TextStyle>
                      )}
                    </div>
                  </div>
                  <Button
                    size="slim"
                    onClick={() => setActiveDepartment(dept.key)}
                  >
                    Manage
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={Boolean(activeDepartment)}
        title="Manage variant weights"
        onClose={() => setActiveDepartment(null)}
        primaryAction={{
          content: "Save",
          onAction: handleSaveWeights,
          loading:
            activeDepartment &&
            saving === `globepos_${activeDepartment}_variant_weights`,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setActiveDepartment(null),
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Supply the weights you want to expose as variants when a matching
              department item is imported.
            </TextStyle>
            <Stack spacing="tight" wrap>
              {weightsDraft.length === 0 ? (
                <TextStyle variation="subdued">
                  No custom weights added.
                </TextStyle>
              ) : (
                weightsDraft.map((descriptor) => (
                  <Tag
                    key={descriptor}
                    onRemove={() => handleRemoveWeight(descriptor)}
                  >
                    {descriptor}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Add weight"
                  value={newWeight}
                  onChange={(value) => {
                    setNewWeight(value);
                    setError(null);
                  }}
                  autoComplete="off"
                  placeholder="e.g. 1kg, 500g, 1lb"
                  error={error}
                />
                <Button onClick={handleAddWeight} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

const DEFAULT_FORCED_WEIGHT_VARIANT_WEIGHTS = ["250g", "500g", "1kg"];

function GlobePosForcedWeightVariantWeightsCard({
  form,
  handleBulkUpdate,
  saving,
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [weightsDraft, setWeightsDraft] = React.useState([]);
  const [newWeight, setNewWeight] = React.useState("");
  const [error, setError] = React.useState(null);

  const fieldName = "globepos_forced_weight_variant_weights";

  const currentWeights = React.useMemo(() => {
    return Array.isArray(form[fieldName]) && form[fieldName].length > 0
      ? form[fieldName]
      : DEFAULT_FORCED_WEIGHT_VARIANT_WEIGHTS;
  }, [form, fieldName]);

  const openModal = () => {
    setWeightsDraft(currentWeights);
    setNewWeight("");
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleAddWeight = () => {
    const trimmed = newWeight.trim();
    if (!trimmed) return;
    if (!isValidWeightDescriptor(trimmed)) {
      setError("Use format like 1kg, 500g, or 1lb (include number and unit).");
      return;
    }
    if (
      weightsDraft.some(
        (descriptor) => descriptor.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setNewWeight("");
      setError(null);
      return;
    }
    setWeightsDraft((current) => [...current, trimmed]);
    setNewWeight("");
    setError(null);
  };

  const handleRemoveWeight = (descriptor) => {
    setWeightsDraft((current) => current.filter((item) => item !== descriptor));
  };

  const handleSaveWeights = () => {
    handleBulkUpdate({ [fieldName]: weightsDraft }, fieldName).then(() =>
      setModalOpen(false),
    );
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Forced Weight Department Variants"
          actions={[
            {
              content: "Manage",
              onAction: openModal,
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Weight variants for items in forced weight departments. These
              weights apply to all departments configured as forced weight,
              independent of produce/meat/deli settings.
            </TextStyle>
            <Stack spacing="tight" wrap>
              {currentWeights.map((value) => (
                <Tag key={`forced-weight-variant-${value}`}>{value}</Tag>
              ))}
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Manage forced weight department variants"
        primaryAction={{
          content: "Save",
          onAction: handleSaveWeights,
          loading: saving === fieldName,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Add weight options that will be created as variants for all forced
              weight department items. Examples: 100g, 250g, 500g, 1kg, 2kg
            </TextStyle>
            <Stack spacing="tight" wrap>
              {weightsDraft.length === 0 ? (
                <TextStyle variation="subdued">
                  No weights configured.
                </TextStyle>
              ) : (
                weightsDraft.map((descriptor) => (
                  <Tag
                    key={`manage-forced-${descriptor}`}
                    onRemove={() => handleRemoveWeight(descriptor)}
                  >
                    {descriptor}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Add weight variant"
                  value={newWeight}
                  onChange={setNewWeight}
                  autoComplete="off"
                  placeholder="e.g., 1kg, 500g, 2lb"
                  error={error}
                />
                <Button onClick={handleAddWeight} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosForceFixedPriceUnitsCard({
  form,
  handleBulkUpdate,
  saving,
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draftValues, setDraftValues] = React.useState([]);
  const [newValue, setNewValue] = React.useState("");

  const normalizeValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return value
      .toString()
      .trim()
      .toLowerCase();
  }, []);

  const currentValues = React.useMemo(() => {
    const raw = Array.isArray(form.globepos_force_fixed_price_units)
      ? form.globepos_force_fixed_price_units
      : [];
    return raw
      .map((value) => normalizeValue(value))
      .filter((value) => value.length > 0);
  }, [form.globepos_force_fixed_price_units, normalizeValue]);

  const openModal = () => {
    setDraftValues(currentValues);
    setNewValue("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleAddValue = () => {
    const normalized = normalizeValue(newValue);
    if (!normalized) return;
    if (draftValues.some((existing) => existing === normalized)) {
      setNewValue("");
      return;
    }
    setDraftValues((prev) => [...prev, normalized]);
    setNewValue("");
  };

  const handleRemove = (value) => {
    setDraftValues((prev) => prev.filter((item) => item !== value));
  };

  const handleSave = () => {
    const unique = Array.from(
      new Set(draftValues.map((value) => normalizeValue(value))),
    ).filter((value) => value.length > 0);

    handleBulkUpdate(
      { globepos_force_fixed_price_units: unique },
      "globepos_force_fixed_price_units",
    ).then(() => {
      setModalOpen(false);
    });
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Force Fixed Price Units"
          actions={[
            {
              content: "Manage",
              onAction: openModal,
            },
          ]}
        />
        <Card.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Items with these PLU units will always be treated as fixed-price
              (each) items, overriding any SCALABLE or PLUType flags.
            </TextStyle>
            {currentValues.length > 0 ? (
              <Stack spacing="tight" wrap>
                {currentValues.map((value) => (
                  <Tag key={`force-fixed-unit-${value}`}>{value}</Tag>
                ))}
              </Stack>
            ) : (
              <TextStyle variation="subdued">
                No units configured.
              </TextStyle>
            )}
          </Stack>
        </Card.Section>
      </Card>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Manage force fixed price units"
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: saving === "globepos_force_fixed_price_units",
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="tight">
            <TextStyle variation="subdued">
              Add units that should bypass weight processing. E.g., ea, each
            </TextStyle>
            <Stack spacing="tight" wrap>
              {draftValues.length === 0 ? (
                <TextStyle variation="subdued">
                  No units configured.
                </TextStyle>
              ) : (
                draftValues.map((value) => (
                  <Tag
                    key={`manage-force-fixed-unit-${value}`}
                    onRemove={() => handleRemove(value)}
                  >
                    {value}
                  </Tag>
                ))
              )}
            </Stack>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Add unit"
                  value={newValue}
                  onChange={setNewValue}
                  autoComplete="off"
                  placeholder="e.g., ea"
                />
                <Button onClick={handleAddValue} primary>
                  Add
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Stack>
        </Modal.Section>
      </Modal>
    </>
  );
}

function GlobePosImportBehaviourCard({
  form,
  setForm,
  handleUpdate,
  saving,
  overwriteTagsEnabled,
  open,
  onToggle,
}) {
  return (
    <Card>
      <Card.Section>
        <Stack alignment="center" distribution="between">
          <TextStyle variation="strong">
            Special File Import Behaviour
          </TextStyle>
          <Button plain onClick={onToggle}>
            {open ? "Hide" : "Show"}
          </Button>
        </Stack>
      </Card.Section>
      <Collapsible open={open} id="globepos-import-behaviour">
        <Card.Section>
          <FormLayout>
            <TextField
              label="Special Import Filename"
              value={form.globepos_special_item_filename || ""}
              onChange={(val) =>
                setForm({ ...form, globepos_special_item_filename: val })
              }
              onBlur={(event) =>
                handleUpdate(
                  "globepos_special_item_filename",
                  event.target.value,
                )
              }
              helpText="Optional override file name that triggers the special import flow."
            />
            <Select
              label="Product Title Source"
              options={[
                {
                  label: "Standard description (DESCRIPTION)",
                  value: "DESCRIPTION",
                },
                {
                  label: "Alternate description (ALTDESCRIPTION)",
                  value: "ALTDESCRIPTION",
                },
                {
                  label: "Extended description (EXTDESCRIPTION)",
                  value: "EXTDESCRIPTION",
                },
              ]}
              value={form.globepos_special_title_source || "DESCRIPTION"}
              onChange={(val) => {
                setForm({ ...form, globepos_special_title_source: val });
                handleUpdate("globepos_special_title_source", val);
              }}
              helpText="Choose which description field from the CSV should be used as the product title. If the selected field is blank for a row, it will fall back to the standard DESCRIPTION field."
            />
            <SettingToggle
              action={{
                content: form.globepos_supplement_pack_size
                  ? "Disable pack size supplement"
                  : "Enable pack size supplement",
                onAction: () =>
                  handleUpdate(
                    "globepos_supplement_pack_size",
                    !form.globepos_supplement_pack_size,
                  ),
                loading: saving === "globepos_supplement_pack_size",
                destructive: form.globepos_supplement_pack_size,
              }}
              enabled={form.globepos_supplement_pack_size}
            >
              Pack size supplement from DESCRIPTION is{" "}
              <Badge
                status={
                  form.globepos_supplement_pack_size ? "success" : "warning"
                }
              >
                {form.globepos_supplement_pack_size ? "Enabled" : "Disabled"}
              </Badge>
              . When enabled and using Alt Description as title source, pack
              size (e.g., "145g", "4pk") will be extracted from DESCRIPTION and
              appended if missing from the alt description.
            </SettingToggle>
            <SettingToggle
              action={{
                content: form.single_variant_promo_pricing
                  ? "Disable single-variant 2-for pricing"
                  : "Enable single-variant 2-for pricing",
                onAction: () =>
                  handleUpdate(
                    "single_variant_promo_pricing",
                    !form.single_variant_promo_pricing,
                  ),
                loading: saving === "single_variant_promo_pricing",
                destructive: form.single_variant_promo_pricing,
              }}
              enabled={form.single_variant_promo_pricing}
            >
              Single-variant "2 for" pricing is{" "}
              <Badge
                status={
                  form.single_variant_promo_pricing ? "success" : "warning"
                }
              >
                {form.single_variant_promo_pricing ? "Enabled" : "Disabled"}
              </Badge>
              . When enabled, a multi-buy promo (e.g., "2 for $6") puts the
              single "Sold by Each" variant on sale at the per-unit price
              ($3.00) with the regular price in compare-at, instead of creating
              a separate "2 for" variant. Existing "2 for" variants are removed
              on the next sync. Applies to GlobePOS special imports for this
              store only.
            </SettingToggle>
          </FormLayout>
        </Card.Section>
        <Card.Section>
          <Stack vertical spacing="loose">
            <SettingToggle
              action={{
                content: form.globepos_overwrite_product_titles
                  ? "Disable title updates"
                  : "Enable title updates",
                onAction: () =>
                  handleUpdate(
                    "globepos_overwrite_product_titles",
                    !form.globepos_overwrite_product_titles,
                  ),
                loading: saving === "globepos_overwrite_product_titles",
                destructive: form.globepos_overwrite_product_titles,
              }}
              enabled={form.globepos_overwrite_product_titles}
            >
              Product title updates from GlobePOS imports are{" "}
              <Badge
                status={
                  form.globepos_overwrite_product_titles ? "success" : "warning"
                }
              >
                {form.globepos_overwrite_product_titles
                  ? "Enabled"
                  : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_prevent_price_updates
                  ? "Allow price updates"
                  : "Prevent price updates",
                onAction: () =>
                  handleUpdate(
                    "globepos_prevent_price_updates",
                    !form.globepos_prevent_price_updates,
                  ),
                loading: saving === "globepos_prevent_price_updates",
                destructive: !form.globepos_prevent_price_updates,
              }}
              enabled={!form.globepos_prevent_price_updates}
            >
              Price updates from GlobePOS imports are{" "}
              <Badge
                status={
                  form.globepos_prevent_price_updates ? "critical" : "success"
                }
              >
                {form.globepos_prevent_price_updates ? "Prevented" : "Allowed"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_sync_inventory
                  ? "Disable inventory sync"
                  : "Enable inventory sync",
                onAction: () =>
                  handleUpdate(
                    "globepos_sync_inventory",
                    !form.globepos_sync_inventory,
                  ),
                loading: saving === "globepos_sync_inventory",
                destructive: form.globepos_sync_inventory,
              }}
              enabled={form.globepos_sync_inventory}
            >
              Inventory syncing from GlobePOS imports is{" "}
              <Badge
                status={form.globepos_sync_inventory ? "success" : "warning"}
              >
                {form.globepos_sync_inventory ? "Enabled" : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_skip_cached_products
                  ? "Re-enable cached products"
                  : "Skip cached products",
                onAction: () =>
                  handleUpdate(
                    "globepos_skip_cached_products",
                    !form.globepos_skip_cached_products,
                  ),
                loading: saving === "globepos_skip_cached_products",
                destructive: form.globepos_skip_cached_products,
              }}
              enabled={!form.globepos_skip_cached_products}
            >
              Cached product lookups during special ITEM file imports are{" "}
              <Badge
                status={
                  form.globepos_skip_cached_products ? "critical" : "success"
                }
              >
                {form.globepos_skip_cached_products ? "Skipped" : "Enabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_skip_weighted_items
                  ? "Enable weight-based updates"
                  : "Skip weight-based updates",
                onAction: () =>
                  handleUpdate(
                    "globepos_skip_weighted_items",
                    !form.globepos_skip_weighted_items,
                  ),
                loading: saving === "globepos_skip_weighted_items",
                destructive: form.globepos_skip_weighted_items,
              }}
              enabled={!form.globepos_skip_weighted_items}
            >
              Weight-based item price and inventory updates during special ITEM
              file imports are{" "}
              <Badge
                status={
                  form.globepos_skip_weighted_items ? "critical" : "success"
                }
              >
                {form.globepos_skip_weighted_items ? "Skipped" : "Enabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_bulk_special_import_enabled
                  ? "Disable bulk importer"
                  : "Enable bulk importer",
                onAction: () =>
                  handleUpdate(
                    "globepos_bulk_special_import_enabled",
                    !form.globepos_bulk_special_import_enabled,
                  ),
                loading: saving === "globepos_bulk_special_import_enabled",
                destructive: form.globepos_bulk_special_import_enabled,
              }}
              enabled={form.globepos_bulk_special_import_enabled}
            >
              Bulk special-item importer is{" "}
              <Badge
                status={
                  form.globepos_bulk_special_import_enabled
                    ? "success"
                    : "warning"
                }
              >
                {form.globepos_bulk_special_import_enabled
                  ? "Enabled"
                  : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_sync_gst_only_collection
                  ? "Disable GST-only collection sync"
                  : "Enable GST-only collection sync",
                onAction: () =>
                  handleUpdate(
                    "globepos_sync_gst_only_collection",
                    !form.globepos_sync_gst_only_collection,
                  ),
                loading: saving === "globepos_sync_gst_only_collection",
                destructive: form.globepos_sync_gst_only_collection,
              }}
              enabled={form.globepos_sync_gst_only_collection}
            >
              Automatic management of the "GST Only Products" collection is{" "}
              <Badge
                status={
                  form.globepos_sync_gst_only_collection ? "success" : "default"
                }
              >
                {form.globepos_sync_gst_only_collection
                  ? "Enabled"
                  : "Disabled"}
              </Badge>
              . When enabled, products with GST but no HST in the POS file will
              be automatically synced to a manual collection for tax overrides.
            </SettingToggle>

            <Stack vertical spacing="tight">
              <p>
                <strong>ITEMFILE TAX column mapping.</strong> Set what each
                TAX column in this store's POS file actually means. Leave
                unmapped to keep the importer on its legacy interpretation.
              </p>
              {["TAX 1", "TAX 2", "TAX 3"].map((column) => (
                <Select
                  key={column}
                  label={column}
                  options={[
                    { label: "(unmapped)", value: "" },
                    { label: "GST", value: "GST" },
                    { label: "PST", value: "PST" },
                    { label: "HST", value: "HST" },
                    { label: "MEMBER (non-taxable upcharge)", value: "MEMBER" },
                  ]}
                  value={form.globepos_tax_column_mapping[column] || ""}
                  onChange={(value) => {
                    const next = { ...form.globepos_tax_column_mapping };
                    if (value) {
                      next[column] = value;
                    } else {
                      delete next[column];
                    }
                    setForm({ ...form, globepos_tax_column_mapping: next });
                  }}
                />
              ))}
              <ButtonGroup>
                <Button
                  primary
                  loading={saving === "globepos_tax_column_mapping"}
                  onClick={() =>
                    handleUpdate(
                      "globepos_tax_column_mapping",
                      form.globepos_tax_column_mapping,
                    )
                  }
                >
                  Save TAX column mapping
                </Button>
                <Button
                  loading={saving === "globepos_tax_column_mapping"}
                  onClick={() => {
                    setForm({ ...form, globepos_tax_column_mapping: {} });
                    handleUpdate("globepos_tax_column_mapping", {});
                  }}
                >
                  Reset to legacy default
                </Button>
              </ButtonGroup>
            </Stack>

            <SettingToggle
              action={{
                content: overwriteTagsEnabled
                  ? "Disable tag overrides"
                  : "Enable tag overrides",
                onAction: () =>
                  handleUpdate(
                    "globepos_overwrite_tags_enabled",
                    !overwriteTagsEnabled,
                  ),
                loading: saving === "globepos_overwrite_tags_enabled",
                destructive: overwriteTagsEnabled,
              }}
              enabled={overwriteTagsEnabled}
            >
              GlobePOS imports will replace product tags with the POS category
              description.{" "}
              <Badge status={overwriteTagsEnabled ? "success" : "warning"}>
                {overwriteTagsEnabled ? "Enabled" : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_update_product_type_enabled
                  ? "Disable product type updates"
                  : "Enable product type updates",
                onAction: () =>
                  handleUpdate(
                    "globepos_update_product_type_enabled",
                    !form.globepos_update_product_type_enabled,
                  ),
                loading: saving === "globepos_update_product_type_enabled",
                destructive: form.globepos_update_product_type_enabled,
              }}
              enabled={form.globepos_update_product_type_enabled}
            >
              GlobePOS imports will update product types from the POS category.{" "}
              <Badge
                status={
                  form.globepos_update_product_type_enabled
                    ? "success"
                    : "warning"
                }
              >
                {form.globepos_update_product_type_enabled
                  ? "Enabled"
                  : "Disabled"}
              </Badge>
              . When disabled, product types are only set if currently blank.
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_new_products_draft
                  ? "Create new products as ACTIVE"
                  : "Create new products as DRAFT",
                onAction: () =>
                  handleUpdate(
                    "globepos_new_products_draft",
                    !form.globepos_new_products_draft,
                  ),
                loading: saving === "globepos_new_products_draft",
                destructive: false,
              }}
              enabled={form.globepos_new_products_draft}
            >
              New products from GlobePOS imports will be created as{" "}
              <Badge
                status={
                  form.globepos_new_products_draft ? "warning" : "success"
                }
              >
                {form.globepos_new_products_draft ? "DRAFT" : "ACTIVE"}
              </Badge>
              {form.globepos_new_products_draft && (
                <>
                  {" "}
                  and tagged with <code>__pending-review__</code> for manual
                  approval. Remove the tag or set status to ACTIVE to approve
                </>
              )}
              .
            </SettingToggle>
          </Stack>
        </Card.Section>
      </Collapsible>
    </Card>
  );
}

function GlobePosNormalImportCard({
  form,
  setForm,
  handleUpdate,
  saving,
  open,
  onToggle,
}) {
  return (
    <Card>
      <Card.Section>
        <Stack alignment="center" distribution="between">
          <TextStyle variation="strong">Normal Import Behaviour</TextStyle>
          <Button plain onClick={onToggle}>
            {open ? "Hide" : "Show"}
          </Button>
        </Stack>
      </Card.Section>
      <Collapsible open={open} id="globepos-normal-import-behaviour">
        <Card.Section>
          <Stack vertical spacing="loose">
            <SettingToggle
              action={{
                content: form.globepos_create_products
                  ? "Disable product creation"
                  : "Enable product creation",
                onAction: () =>
                  handleUpdate(
                    "globepos_create_products",
                    !form.globepos_create_products,
                  ),
                loading: saving === "globepos_create_products",
                destructive: form.globepos_create_products,
              }}
              enabled={form.globepos_create_products}
            >
              Product creation for normal imports is{" "}
              <Badge
                status={form.globepos_create_products ? "warning" : "success"}
              >
                {form.globepos_create_products ? "Enabled" : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_bulk_grocerbox_enabled
                  ? "Disable bulk importer"
                  : "Enable bulk importer",
                onAction: () =>
                  handleUpdate(
                    "globepos_bulk_grocerbox_enabled",
                    !form.globepos_bulk_grocerbox_enabled,
                  ),
                loading: saving === "globepos_bulk_grocerbox_enabled",
                destructive: form.globepos_bulk_grocerbox_enabled,
              }}
              enabled={form.globepos_bulk_grocerbox_enabled}
            >
              Bulk Grocerbox importer is{" "}
              <Badge
                status={
                  form.globepos_bulk_grocerbox_enabled ? "success" : "warning"
                }
              >
                {form.globepos_bulk_grocerbox_enabled ? "Enabled" : "Disabled"}
              </Badge>
              . Bulk imports are 3-5x faster and use fewer API calls.
            </SettingToggle>

            <SettingToggle
              action={{
                content: form.globepos_grocerbox_variant_expansion_enabled
                  ? "Disable variant expansion"
                  : "Enable variant expansion",
                onAction: () =>
                  handleUpdate(
                    "globepos_grocerbox_variant_expansion_enabled",
                    !form.globepos_grocerbox_variant_expansion_enabled,
                  ),
                loading:
                  saving === "globepos_grocerbox_variant_expansion_enabled",
                destructive: form.globepos_grocerbox_variant_expansion_enabled,
              }}
              enabled={form.globepos_grocerbox_variant_expansion_enabled}
            >
              Multi-variant weight expansion for produce/meat/deli is{" "}
              <Badge
                status={
                  form.globepos_grocerbox_variant_expansion_enabled
                    ? "success"
                    : "default"
                }
              >
                {form.globepos_grocerbox_variant_expansion_enabled
                  ? "Enabled"
                  : "Disabled"}
              </Badge>
              . When enabled, weighted items in produce, meat, and deli
              departments will be created with multiple weight variants using
              the shared Variant Weights config above.
            </SettingToggle>
          </Stack>
        </Card.Section>
      </Collapsible>
    </Card>
  );
}

function GlobePosMonitoringCard({
  form,
  setForm,
  handleUpdate,
  saving,
  selectedShop,
  open,
  onToggle,
}) {
  return (
    <Card>
      <Card.Section>
        <Stack alignment="center" distribution="between">
          <TextStyle variation="strong">Monitoring &amp; Support</TextStyle>
          <Button plain onClick={onToggle}>
            {open ? "Hide" : "Show"}
          </Button>
        </Stack>
      </Card.Section>
      <Collapsible open={open} id="globepos-monitoring-support">
        <Card.Section>
          <FormLayout>
            <TextField
              label="Support email"
              type="email"
              value={form.globepos_support_email || ""}
              onChange={(value) =>
                setForm({ ...form, globepos_support_email: value })
              }
              onBlur={(event) =>
                handleUpdate("globepos_support_email", event.target.value)
              }
              helpText="Alerts will be sent here if a daily GlobePOS import is missing."
              autoComplete="email"
            />
          </FormLayout>
        </Card.Section>
        <Card.Section>
          <Stack vertical spacing="loose">
            <SettingToggle
              action={{
                content: form.globepos_import_monitor_enabled
                  ? "Disable monitoring"
                  : "Enable monitoring",
                onAction: () =>
                  handleUpdate(
                    "globepos_import_monitor_enabled",
                    !form.globepos_import_monitor_enabled,
                  ),
                loading: saving === "globepos_import_monitor_enabled",
                destructive: form.globepos_import_monitor_enabled,
              }}
              enabled={form.globepos_import_monitor_enabled}
            >
              Daily GlobePOS import monitoring is{" "}
              <Badge
                status={
                  form.globepos_import_monitor_enabled ? "success" : "warning"
                }
              >
                {form.globepos_import_monitor_enabled ? "Enabled" : "Disabled"}
              </Badge>
              .
            </SettingToggle>

            {selectedShop &&
            selectedShop.shopify_domain !== "onefinefood.myshopify.com" ? (
              <SettingToggle
                action={{
                  content: form.globepos_inventory_management_enabled
                    ? "Disable"
                    : "Enable",
                  onAction: () =>
                    handleUpdate(
                      "globepos_inventory_management_enabled",
                      !form.globepos_inventory_management_enabled,
                    ),
                  loading: saving === "globepos_inventory_management_enabled",
                  destructive: form.globepos_inventory_management_enabled,
                }}
                enabled={form.globepos_inventory_management_enabled}
              >
                POS Inventory management is{" "}
                <Badge
                  status={
                    form.globepos_inventory_management_enabled
                      ? "success"
                      : "default"
                  }
                >
                  {form.globepos_inventory_management_enabled
                    ? "Enabled"
                    : "Disabled"}
                </Badge>
                .
              </SettingToggle>
            ) : null}
          </Stack>
        </Card.Section>
      </Collapsible>
    </Card>
  );
}

function buildDraftState(keys, source) {
  return keys.reduce((accumulator, key) => {
    accumulator[key] = source[key] || "";
    return accumulator;
  }, {});
}

